import { Component } from '@theme/component';
import { fetchConfig, debounce, onAnimationEnd, prefersReducedMotion, resetShimmer } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

// ── ADDED: Bundle sync helpers ────────────────────────────────────────────────
/**
 * Fetch fresh cart items from server (never stale DOM data)
 * @returns {Promise<Array>}
 */
async function fetchCartItems() {
  const res = await fetch('/cart.js', { cache: 'no-store' });
  const data = await res.json();
  return data.items || [];
}

/**
 * Get bundle info for a line item by 1-based index
 * @param {Array} items
 * @param {number} line
 * @returns {{ bundleUuid: string|null, isMainChair: boolean }}
 */
function getBundleInfo(items, line) {
  const item = items[line - 1];
  if (!item) return { bundleUuid: null, isMainChair: false };
  
  /* Direct check on this line item */
  if (item.properties?._main_chair_product === 'true') {
    return {
      bundleUuid: item.properties?._bundle_uuid || null,
      isMainChair: true,
    };
  }
  
  /*
   * ADDED: If this item is an accessory (_chair_accessory=true),
   * find its main chair by matching _bundle_uuid.
   * This handles cases where accessories appear before chair in cart.
   * Return isMainChair=false so caller knows this isn't the chair,
   * but bundleUuid is still returned for reference.
   */
  if (item.properties?._chair_accessory === 'true') {
    return {
      bundleUuid: item.properties?._bundle_uuid || null,
      isMainChair: false,
    };
  }
  
  return { bundleUuid: null, isMainChair: false };
}

/**
 * Sync accessory quantities using item KEY (immune to line index shifts).
 * @param {Array} items
 * @param {string} bundleUuid
 * @param {number} newQty
 * @returns {Promise<void>}
 */
async function syncBundleAccessories(items, bundleUuid, newQty) {
  if (!bundleUuid) return;
  const updates = {};
  for (const item of items) {
    const uuid  = item.properties?._bundle_uuid || '';
    const isAcc = item.properties?._chair_accessory === 'true';
    if (isAcc && uuid === bundleUuid) {
      updates[item.key] = newQty;
    }
  }
  if (Object.keys(updates).length === 0) return;
  await fetch('/cart/update.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ updates }),
  });
}
// ─────────────────────────────────────────────────────────────────────────────

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    const { quantity, cartLine: line } = event.detail;

    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    // ── ADDED: Remove accessories with same bundle_uuid ──────────
    fetchCartItems()
      .then(async (items) => {
        const { bundleUuid, isMainChair } = getBundleInfo(items, line);
        
        /*
         * FIXED: If removing an accessory that belongs to a bundle,
         * find the main chair key BEFORE removing anything.
         */
        let mainChairKey = null;
        if (!isMainChair && bundleUuid) {
          const mainChair = items.find(i =>
            i.properties?._bundle_uuid === bundleUuid &&
            i.properties?._main_chair_product === 'true'
          );
          mainChairKey = mainChair?.key || null;
        }
        
        // Remove main item first
        await fetch(Theme.routes.cart_change_url, fetchConfig('json', {
          body: JSON.stringify({ line, quantity: 0, sections_url: window.location.pathname }),
        }));
        // Then remove accessories if this was a main chair
        if (isMainChair && bundleUuid) {
          const fresh = await fetchCartItems();
          await syncBundleAccessories(fresh, bundleUuid, 0);
        }
        // Refresh UI
        sectionRenderer.renderSection(this.sectionId, { cache: false });
      })
      .catch(() => {
        // Fallback to original behavior
        this.updateQuantity({ line, quantity: 0, action: 'clear' });
      });
    // ─────────────────────────────────────────────────────────────

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    // ── ADDED: Sync accessory quantities when main chair changes ─
    // FIXED: Verify actual quantity from server AFTER change before syncing
    // This prevents accessories from syncing when main chair hits stock limit
    if (quantity > 0) {
      fetchCartItems()
        .then(async (itemsBefore) => {
          const { bundleUuid, isMainChair } = getBundleInfo(itemsBefore, line);

          if (!bundleUuid) return;

          if (!isMainChair) {
            const thisItem = itemsBefore[line - 1];
            const isThisAccessory = thisItem?.properties?._chair_accessory === 'true';
            if (!isThisAccessory) return;

            const mainChair = itemsBefore.find(i =>
              i.properties?._bundle_uuid === bundleUuid &&
              i.properties?._main_chair_product === 'true'
            );
            if (!mainChair) return;

            const mainQty = mainChair.quantity;
            if (mainQty === quantity) return;

            await new Promise(resolve => setTimeout(resolve, 1000));
            const itemsAfter = await fetchCartItems();
            await syncBundleAccessories(itemsAfter, bundleUuid, mainQty);
            sectionRenderer.renderSection(this.sectionId, { cache: false });
            return;
          }

          const itemBefore    = itemsBefore[line - 1];
          const itemBeforeKey = itemBefore?.key;
          const qtyBefore     = itemBefore?.quantity || 0;

          /* Wait for Shopify to process the change */
          await new Promise(resolve => setTimeout(resolve, 1000));

          try {
            const itemsAfter = await fetchCartItems();
            const itemAfter  = itemsAfter.find(i => i.key === itemBeforeKey);
            const actualNewQty = itemAfter?.quantity;

            console.log('Bundle sync check — before:', qtyBefore, 'after:', actualNewQty, 'requested:', quantity);

            if (actualNewQty === undefined) {
              console.log('Bundle: item not found after change, skipping sync');
              return;
            }

            if (actualNewQty !== qtyBefore) {
              await syncBundleAccessories(itemsAfter, bundleUuid, actualNewQty);
              console.log('Bundle: accessories synced to qty', actualNewQty);
              sectionRenderer.renderSection(this.sectionId, { cache: false });
            } else {
              console.log('Bundle: qty unchanged on server (stock limit?), skipping');
            }
          } catch (e) {
            console.error('Accessory quantity sync error', e);
          }
        })
        .catch((e) => console.error('fetchCartItems error', e));
    }
    // ─────────────────────────────────────────────────────────────

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        this.dispatchEvent(
          new CartUpdateEvent({}, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId]);
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);
      // Update videos after morphing
      this.updateCartVideos();
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      // Update videos after rendering
      setTimeout(() => this.updateCartVideos(), 100);
    }
  };

  /**
   * Updates cart videos to show correct variant-specific videos.
   * FIXED: Single /cart.js fetch for all rows to prevent race condition
   * causing wrong color media after quantity change.
   */
  updateCartVideos() {
    // Find all cart item rows in this component
    const cartRows = this.querySelectorAll('.cart-items__table-row');
    if (!cartRows.length) return;

    // ── FIXED: Single cart fetch for ALL rows (prevents race condition) ──
    fetch('/cart.js', { cache: 'no-store' })
      .then(response => response.json())
      .then(cart => {
        cartRows.forEach(row => {
          const mediaContainer = row.querySelector('.cart-items__media-container');
          const existingVideo  = mediaContainer?.querySelector('.cart-items__media-video');
          const existingImage  = mediaContainer?.querySelector('.cart-items__media-image');

          if (!mediaContainer) return;

          // Get the cart item key to identify the specific item
          const itemKey = row.dataset.key;
          if (!itemKey) return;

          // Find the correct cart item by KEY (not index - immune to reordering)
          const cartItem = cart.items.find(item => item.key === itemKey);
          if (!cartItem) return;

          // Look for video in product media that matches the cart item's image
          const productHandle = cartItem.handle;
          if (!productHandle) return;

          // Fetch product data to get media
          fetch(`/products/${productHandle}.js`)
            .then(response => response.json())
            .then(product => {
              // Find video that matches the cart item's variant image
              const variantImage = cartItem.image;
              if (!variantImage) return;

              const matchingVideo = product.media.find(media =>
                (media.media_type === 'video' || media.media_type === 'external_video') &&
                media.preview_image &&
                media.preview_image.src === variantImage
              );

              if (matchingVideo && existingVideo) {
                // Update video sources
                const videoElement = existingVideo;
                videoElement.innerHTML = '';
                videoElement.muted = true;
                videoElement.autoplay = true;
                videoElement.loop = true;
                videoElement.playsInline = true;
                videoElement.setAttribute('playsinline', '');
                videoElement.setAttribute('webkit-playsinline', '');
                if (matchingVideo.sources) {
                  matchingVideo.sources.forEach(source => {
                    const sourceElement = document.createElement('source');
                    sourceElement.src  = source.url;
                    sourceElement.type = source.mime_type;
                    videoElement.appendChild(sourceElement);
                  });
                }
                // Reload the video
                videoElement.load();
                videoElement.play?.().catch(() => {});
              } else if (matchingVideo && existingImage) {
                // Replace image with video
                const videoElement       = document.createElement('video');
                videoElement.className   = 'cart-items__media-video border-style';
                videoElement.style.cssText = existingImage.style.cssText;
                videoElement.autoplay    = true;
                videoElement.loop        = true;
                videoElement.muted       = true;
                videoElement.playsInline = true;
                if (matchingVideo.sources) {
                  matchingVideo.sources.forEach(source => {
                    const sourceElement = document.createElement('source');
                    sourceElement.src  = source.url;
                    sourceElement.type = source.mime_type;
                    videoElement.appendChild(sourceElement);
                  });
                }
                existingImage.replaceWith(videoElement);
                videoElement.load();
                videoElement.play?.().catch(() => {});
              }
            })
            .catch(error => console.log('Error fetching product:', error));
        });
      })
      .catch(error => console.log('Error fetching cart for videos:', error));
  }

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}